import { Editor, EditorProps } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { editorToolbarConfig } from "../constants/editor-toolbar";
import { fromHtmlToEditorState } from "../utils/from-html-to-editor-state";
import { fromEditorStateToHtml } from "../utils/from-editor-state-to-html";
import { EditorState } from "draft-js";
import { useDebouncedCallback } from "use-debounce";

export type WysiwygProps = EditorProps & {
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  toolbarCustomButtonsWithEditorState?: (
    editorState: EditorState,
    setEditorState: (editorState: EditorState) => void,
  ) => EditorProps["toolbarCustomButtons"];
};

export function Wysiwyg({
  onChange,
  value,
  placeholder,
  className,
  toolbarCustomButtonsWithEditorState,
  onBlur,
  ...rest
}: WysiwygProps) {
  const [editorState, setEditorState] = useState(() =>
    fromHtmlToEditorState(value),
  );

  // Последнее значение, отданное наружу: по нему отличаем своё же эхо
  // от значения, пришедшего извне (setFields формы, автоперевод, сброс).
  const emittedValue = useRef(value);

  const updateOuterState = useDebouncedCallback((editorState: EditorState) => {
    const html = fromEditorStateToHtml(editorState);
    emittedValue.current = html;
    onChange?.(html);
  }, 1000);

  useEffect(() => {
    if (value === emittedValue.current) return;

    // Отложенная правка устарела и перетёрла бы новое значение
    updateOuterState.cancel();
    emittedValue.current = value;
    setEditorState(fromHtmlToEditorState(value));
  }, [value, updateOuterState]);

  const onEditorStateChange = (editorState: EditorState) => {
    setEditorState(editorState);
    updateOuterState(editorState);
  };

  return (
    <div
      className={twMerge(
        "!p-2",
        "border-[--ant-color-border] border-solid border rounded-[--ant-border-radius]",
        "[&_.rdw-editor-toolbar]:!border-none",
        "[&_.rdw-editor-toolbar]:!pl-0",
        "[&_.rdw-editor-main]:!pl-3",
        className,
      )}
    >
      <Editor
        editorState={editorState}
        placeholder={placeholder}
        toolbar={editorToolbarConfig}
        toolbarCustomButtons={toolbarCustomButtonsWithEditorState?.(
          editorState,
          onEditorStateChange,
        )}
        onEditorStateChange={onEditorStateChange}
        onBlur={(event) => {
          // Значение уходит в форму с задержкой; при потере фокуса (в т.ч.
          // по клику на кнопку отправки) отдаём его сразу, иначе форма
          // уйдёт на сервер с устаревшим или пустым полем.
          updateOuterState.flush();
          onBlur?.(event);
        }}
        editorClassName="!min-h-[200px] !max-h-[60vh]"
        {...rest}
      />
    </div>
  );
}
